import { Trans, useTranslation } from "react-i18next";
import { useDeferredValue, useMemo, useState, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import type { Sensor } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { cn } from "@/lib/utils";
import type { AuthSession } from "../../auth/keycloak.js";
import { useShowError } from "../../error/ErrorProvider.js";
import { FilterField } from "../roles/FilterField.js";
import { SensorDetailPanel } from "./SensorDetailPanel.js";
import { useFacilityNavParams } from "../facilities/drilldown/useFacilityNavParams.js";
import { SENSOR_TAB_LABEL_KEY, sensorLabel, sensorTabCounts, sensorsForTab, type SensorTab } from "./sensor-model.js";
import { SensorsForbiddenError, useDeleteSensor, useSensor, useSensors } from "./useSensors.js";

/**
 * "Manage sensors" (ADR-035) — the legacy `settings_sensors` screen: two type tabs + a list on the left, the selected
 * sensor's detail on the right. READ + DELETE only, because devices register themselves (the legacy had no create and
 * no edit either). SuperAdmin-only; a 403 is the verdict, shown as a plain no-access line.
 *
 * Beyond the legacy: a name filter (42 devices today with no other way to find one) and a delete failure that is
 * SHOWN — the legacy `console.log`ged it and left the operator staring at an unchanged list.
 */
export function SensorsView({ session, heading }: { session: AuthSession; heading?: ReactNode }) {
  const { t } = useTranslation();
  const nav = useFacilityNavParams();
  const sensors = useSensors(session);
  const [tab, setTab] = useState<SensorTab>("sensors");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [term, setTerm] = useState("");
  const [confirming, setConfirming] = useState<Sensor | null>(null);
  const deferredTerm = useDeferredValue(term);
  const detail = useSensor(session, selectedId);
  const remove = useDeleteSensor(session);
  const showError = useShowError();

  const all = useMemo(() => sensors.data ?? [], [sensors.data]);
  const counts = useMemo(() => sensorTabCounts(all, deferredTerm), [all, deferredTerm]);
  const rows = useMemo(() => sensorsForTab(all, tab, deferredTerm), [all, tab, deferredTerm]);

  const withHeading = (body: ReactNode) => (
    <div className="flex flex-col gap-4">
      {heading}
      {body}
    </div>
  );

  if (sensors.isPending) {
    return withHeading(
      <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("sensors.loadingSensors")}
      </p>,
    );
  }
  if (sensors.error instanceof SensorsForbiddenError) {
    return withHeading(<p className="text-muted-foreground max-w-prose text-sm">{t("errors.sensorsForbidden")}</p>);
  }
  if (sensors.isError) {
    return withHeading(
      <div className="flex flex-col items-start gap-2">
        <p role="alert" className="text-destructive text-sm">
          {t("sensors.couldntLoadTheSensors")}
        </p>
        <Button variant="outline" size="sm" onPress={() => void sensors.refetch()}>
          {t("sensors.tryAgain")}
        </Button>
      </div>,
    );
  }
  if (all.length === 0) {
    return withHeading(
      <p className="text-muted-foreground max-w-prose text-sm">
        {t("sensors.noSensorsRegistered")}
      </p>,
    );
  }

  const confirmDelete = () => {
    const sensor = confirming;
    if (!sensor) return;
    remove.mutate(
      { id: sensor.id },
      {
        onSuccess: () => {
          setConfirming(null);
          if (selectedId === sensor.id) setSelectedId(null);
        },
        onError: (error) => {
          setConfirming(null);
          // Straight to the shared error surface: `SENSOR_IN_USE` resolves to its copy AND lists the blocking manuals
          // (resolveError). The legacy swallowed a failed delete into console.log and left the list unchanged.
          showError(error);
        },
      },
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 md:flex-row">
      <div className="border-border flex shrink-0 flex-col gap-3 md:w-80 md:overflow-y-auto md:border-r md:pr-3 lg:w-96">
        {heading}
        <div className="flex gap-2" role="tablist" aria-label={t("sensors.sensorType")}>
          {(["sensors", "handSpeed"] as const).map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              role="tab"
              aria-selected={tab === tabKey}
              onClick={() => setTab(tabKey)}
              className={cn(
                "focus-visible:ring-ring/50 flex cursor-pointer items-center gap-1.5 rounded-full border-0 px-3 py-1 text-xs font-medium outline-none focus-visible:ring-2",
                tab === tabKey ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground",
              )}
            >
              {t(SENSOR_TAB_LABEL_KEY[tabKey])}
              <span className={cn("rounded-full px-1.5 text-[0.65rem]", tab === tabKey ? "bg-primary-foreground/20" : "bg-background/70")}>{counts[tabKey]}</span>
            </button>
          ))}
        </div>
        <FilterField label={t("sensors.filterSensorsByName")} placeholder={t("sensors.filterByNameOrId")} value={term} onChange={setTerm} />
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{term.trim() === "" ? t("sensors.noSensorsOfThisType") : t("sensors.noSensorMatches", { query: term.trim() })}</p>
        ) : (
          <ul aria-label={t(SENSOR_TAB_LABEL_KEY[tab])} className="m-0 flex list-none flex-col gap-0.5 p-0">
            {rows.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  aria-current={s.id === selectedId ? "true" : undefined}
                  onClick={() => setSelectedId(s.id === selectedId ? null : s.id)}
                  className={cn(
                    "hover:bg-muted focus-visible:ring-ring/50 flex w-full cursor-pointer items-center gap-2 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-sm outline-none focus-visible:ring-2",
                    s.id === selectedId && "bg-muted",
                  )}
                >
                  <span className="font-semibold tabular-nums">{s.slug}</span>
                  <span className="min-w-0 flex-1 truncate">{sensorLabel(s)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <section aria-label={t("sensors.sensorDetails")} className="min-w-0 flex-1 md:overflow-y-auto">
        {selectedId === null ? (
          <p className="text-muted-foreground text-sm">{t("sensors.pleaseSelectASensor")}</p>
        ) : detail.isPending ? (
          <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" /> {t("sensors.sensorIsLoading")}
          </p>
        ) : detail.isError || !detail.data ? (
          <div className="flex flex-col items-start gap-2">
            <p role="alert" className="text-destructive text-sm">
              {t("sensors.couldntLoadThisSensor")}
            </p>
            <Button variant="outline" size="sm" onPress={() => void detail.refetch()}>
              {t("sensors.tryAgain")}
            </Button>
          </div>
        ) : (
          <SensorDetailPanel
            detail={detail.data}
            onDelete={() => setConfirming(detail.data.sensor)}
            deleteDisabled={remove.isPending}
            // Leaves the workspace for the manual's own facility view with its details drawer open — the same deep
            // link the dashboard's lists and the linked-manual cards use (ADR-020).
            onOpenManual={(facilityId, manualId) => nav.openManualDetails(facilityId, manualId)}
          />
        )}
      </section>

      <ConfirmDialog
        isOpen={confirming !== null}
        title={t("sensors.deleteButton")}
        message={
          <>
            <Trans i18nKey="sensors.confirmDeleteSensor" components={{ name: <strong>{confirming ? sensorLabel(confirming) : ""}</strong> }} />
            <br />
            <Trans i18nKey="sensors.deleteSensorNote" components={{ b: <strong /> }} />
          </>
        }
        confirmLabel={t("other.deleteButton")}
        destructive
        isPending={remove.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setConfirming(null)}
      />
    </div>
  );
}
